<template>
  <div class="container">
    <nav class="nav nav-pills" v-if="state.isLoaded">
      <div class="nav nav-tabs" id="nav-tab" role="tablist">
        <button
          v-for="(category, categoryKey, index) in state.categories"
          :key="categoryKey"
          class="nav-link"
          :class="{ active: index === store.nowTab }"
          :id="categoryKey + '-tab'"
          data-bs-toggle="tab"
          :data-bs-target="'#nav-' + categoryKey"
          type="button"
          role="tab"
          :aria-controls="'nav-' + categoryKey"
          :aria-selected="index === store.nowTab"
          @click="setNowTab(index)"
        >
          {{ getCategoryName(category) }}
        </button>
      </div>
    </nav>

    <div class="tab-content tab-dis" id="nav-tabContent" v-if="state.isLoaded">
      <div
        v-for="(category, categoryKey, index) in state.categories"
        :key="'nav-' + categoryKey"
        class="tab-pane fade"
        :class="{ 'show active': index === store.nowTab }"
        :id="'nav-' + categoryKey"
        role="tabpanel"
        :aria-labelledby="categoryKey + '-tab'"
      >
        <div class="cards row row-cols-2 row-cols-md-3 row-cols-xl-5 g-3">
          <div
            v-for="(bookObject, bookIndex) in category.books"
            :key="categoryKey + bookIndex"
            class="col"
          >
            <router-link
              :to="{
                name: 'BookPage',
                params: {
                  IconButtonId: bookPageId,
                  BookPageId: bookObject.id,
                },
              }"
              class="book-card-link"
              :aria-label="getBookTitle(bookObject)"
            >
              <article class="card book-card h-100">
                <div class="book-image-wrap">
                  <img
                    :src="getImageUrl(bookObject.img)"
                    class="card-img-top"
                    :alt="getBookTitle(bookObject)"
                  />

                  <!-- Google Sheet 的 isNew 勾選後顯示 NEW 圖示 -->
                  <img
                    v-if="isNewBook(bookObject)"
                    :src="`${baseUrl}images/img-1/Product/NEW.png`"
                    class="new-badge"
                    alt="New"
                  />
                </div>

                <div class="card-body">
                  <div class="book-title-group">
                    <h2 class="book-title-main">
                      {{ getBookTitleParts(bookObject).main }}
                    </h2>

                    <p
                      v-if="getBookTitleParts(bookObject).sub"
                      class="book-title-sub"
                    >
                      {{ getBookTitleParts(bookObject).sub }}
                    </p>
                  </div>

                  <p class="card-grade">{{ getBookGrade(bookObject) }}</p>
                </div>
              </article>
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="text-center py-5">Loading...</div>
  </div>
</template>

<script>
import { useRoute } from "vue-router";
import {
  reactive,
  onMounted,
  computed,
  watch,
} from "vue";
import { tabStore } from "../store/index.js";

export default {
  name: "ProductTab",

  setup() {
    const route = useRoute();
    const store = tabStore();

    const state = reactive({
      categories: {},
      isLoaded: false,
    });

    // 目前分類頁代號，例如 MathPage、PhonicsPage
    const bookPageId = computed(() => {
      return route.params.IconButtonId || "";
    });

    // 目前語言
    const currentLang = computed(() => {
      return localStorage.getItem("lang") || "zh";
    });

    // public 資料夾的基底路徑（支援部署在子目錄）
    const baseUrl = process.env.BASE_URL;

    const fetchProductData = async () => {
      state.isLoaded = false;
      state.categories = {};

      try {
        const sheetID =
          "1QX6WfJNRNiY2XLcPZLVjj0kyN2h0RO31GaSmqn_9JB8";

        const sheetName = bookPageId.value;

        if (!sheetName) {
          throw new Error("缺少教材分類代號 IconButtonId");
        }

        const url =
          `https://sheets.googleapis.com/v4/spreadsheets/` +
          `${sheetID}/values/${encodeURIComponent(sheetName)}` +
          `?alt=json&key=AIzaSyD21a6U3dc4wpee6BCu4D-pxnPff9QSW4w`;

        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`Google Sheet 請求失敗：${response.status}`);
        }

        const data = await response.json();

        if (data.values && data.values.length > 1) {
          formatArrayDataToObject(data.values);
        }
      } catch (error) {
        console.error("讀取 Google Sheet 失敗：", error);
      } finally {
        state.isLoaded = true;
      }
    };

    onMounted(() => {
      fetchProductData();
    });

    // 同一元件切換分類路由時，重新抓取資料
    watch(
      () => route.params.IconButtonId,
      (newId, oldId) => {
        if (newId && newId !== oldId) {
          store.nowTab = 0;
          fetchProductData();
        }
      },
    );



    function formatArrayDataToObject(dataList) {
      const objectKey = dataList[0];
      const grouped = {};

      dataList.forEach((item, rowIndex) => {
        if (rowIndex === 0) return;

        const bookObject = {};

        objectKey.forEach((keyItem, colIndex) => {
          bookObject[keyItem] = item[colIndex] || "";
        });

        const categoryKey = bookObject.category_key || "default";

        if (!grouped[categoryKey]) {
          grouped[categoryKey] = {
            label_zh: bookObject.subCategory_zh || "",
            label_en: bookObject.subCategory_en || "",
            books: [],
          };
        }

        grouped[categoryKey].books.push(bookObject);
      });

      state.categories = grouped;
    }

    const getCategoryName = (category) => {
      return currentLang.value.startsWith("zh")
        ? category.label_zh || category.label_en
        : category.label_en || category.label_zh;
    };

    const getBookTitle = (bookObject) => {
      return currentLang.value.startsWith("zh")
        ? bookObject.title_zh || bookObject.title_en
        : bookObject.title_en || bookObject.title_zh;
    };

    const getBookTitleParts = (bookObject) => {
  const isZh = currentLang.value.startsWith("zh");

  return {
    main: isZh
      ? bookObject.title_zh || bookObject.title_en || ""
      : bookObject.title_en || bookObject.title_zh || "",

    sub: isZh
      ? bookObject.subtitle_zh || bookObject.subtitle_en || ""
      : bookObject.subtitle_en || bookObject.subtitle_zh || "",
  };
};

    const getBookGrade = (bookObject) => {
      return currentLang.value.startsWith("zh")
        ? bookObject.grade_zh || bookObject.grade_en
        : bookObject.grade_en || bookObject.grade_zh;
    };

    const getImageUrl = (imageUrl) => {
      if (!imageUrl) return "";

      if (/^(https?:)?\/\//i.test(imageUrl)) {
        return imageUrl;
      }

      return `${process.env.BASE_URL}${imageUrl.replace(/^\//, "")}`;
    };

    // Google Sheet 核取方塊透過 Sheets API 通常會回傳字串 "TRUE" / "FALSE"
    const isNewBook = (bookObject) => {
      const value = bookObject.isNew;

      if (typeof value === "boolean") return value;

      return String(value || "")
        .trim()
        .toLowerCase() === "true";
    };

    const setNowTab = (index) => {
      store.nowTab = index;
    };

    return {
      state,
      store,
      currentLang,
      baseUrl,
      getCategoryName,
      bookPageId,
      getBookTitle,
      getBookTitleParts,
      getBookGrade,
      getImageUrl,
      isNewBook,
      setNowTab,
    };
  },
};
</script>

<style>
.nav-tabs {
  background-color: #0071bc;
  color: white;
}

.nav-link.active {
  background-color: #00489b !important;
}

.tab-dis {
  margin: 15px 0;
}

/* 整張卡片皆可點擊 */
.book-card-link {
  display: block;
  height: 100%;
  color: inherit;
  text-decoration: none;
  border-radius: 8px;
}

.book-card {
  height: 100%;
  overflow: hidden;
  border: 1px solid #d9dee5;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 5px 12px rgba(29, 55, 79, 0.12);
  cursor: pointer;
  transition:
    transform 0.22s ease,
    box-shadow 0.22s ease,
    border-color 0.22s ease;
}

.book-card-link:hover .book-card {
  transform: translateY(-4px);
  border-color: #8fb5dd;
  box-shadow: 0 10px 20px rgba(29, 55, 79, 0.18);
}

.book-card-link:focus-visible {
  outline: 3px solid rgba(0, 72, 155, 0.28);
  outline-offset: 4px;
}

/* 圖片區縮短 */
.book-image-wrap {
  position: relative;
  display: flex;
  height: 270px;
  padding: 10px;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #fff;
}

.book-image-wrap .card-img-top {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* NEW 標籤：顯示在書封圖片區右上角 */
.new-badge {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 58px;
  height: 58px;
  object-fit: contain;
  z-index: 5;
  pointer-events: none;
}

/* 文字區縮短 */
.book-card .card-body {
  display: flex;
  min-height: 145px;
  padding: 14px 14px 12px;
  flex-direction: column;
  text-align: center;
}

/* 大標＋小標區 */
.book-title-group {
  display: flex;
  min-height: 78px;
  flex-direction: column;
  justify-content: flex-start;
}

/* 大標 */
.book-title-main {
  display: -webkit-box;
  min-height: 46px;
  margin: 0;
  overflow: hidden;
  color: #17212b;
  font-size: 18px;
  font-weight: 750;
  line-height: 1.35;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

/* 小標 */
.book-title-sub {
  display: -webkit-box;
  min-height: 22px;
  margin: 4px 0 0;
  overflow: hidden;
  color: #52606d;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
}

/* 年級往上收 */
.card-grade {
  margin: auto 0 0;
  padding-top: 10px;
  color: #555;
  font-size: 14px;
}

.cards {
  margin: 10px 0 30px 0 !important;
}

@media (max-width: 1000px) {
  .book-image-wrap {
    height: 235px;
  }

  .book-card .card-body {
    min-height: 135px;
    padding: 12px 10px 10px;
  }

  .book-title-group {
    min-height: 72px;
  }

  .book-title-main {
    min-height: 42px;
    font-size: 16px;
    line-height: 1.35;
  }

  .book-title-sub {
    min-height: 20px;
    font-size: 13px;
  }

  .card-grade {
    padding-top: 8px;
  }
}

@media (max-width: 575px) {
  .new-badge {
    top: 4px;
    right: 4px;
    width: 46px;
    height: 46px;
  }

  .book-image-wrap {
    height: 195px;
    padding: 8px;
  }

  .book-card .card-body {
    min-height: 125px;
    padding: 10px 8px 9px;
  }

  .book-title-group {
    min-height: 66px;
  }

  .book-title-main {
    min-height: 40px;
    font-size: 15px;
    line-height: 1.35;
  }

  .book-title-sub {
    min-height: 18px;
    font-size: 12px;
  }

  .card-grade {
    padding-top: 7px;
    font-size: 13px;
  }
}
</style>
